import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users, Search, Download, Upload, Plus, ArrowLeft, Pencil, Trash2,
  Save, Loader2, X, Mail, Send, Filter, Tag, UserCheck, Gamepad2,
  CalendarDays, Phone, MapPin, Clock, ChevronDown
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const api = axios.create({ baseURL: API });

const SOURCE_LABELS = {
  jeu: { label: 'Jeu fidelite', color: 'bg-purple-500/15 text-purple-400' },
  reservation: { label: 'Reservation', color: 'bg-blue-500/15 text-blue-400' },
  import: { label: 'Import CSV', color: 'bg-yellow-500/15 text-yellow-400' },
  manuel: { label: 'Manuel', color: 'bg-[rgb(var(--lm-gold)/0.15)] text-[rgb(var(--lm-gold))]' },
  bon_cadeau: { label: 'Bon cadeau', color: 'bg-pink-500/15 text-pink-400' },
  zenchef: { label: 'Zenchef', color: 'bg-rose-500/15 text-rose-400' },
};

const ClientsPage = () => {
  const [tab, setTab] = useState('dashboard');
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [optInFilter, setOptInFilter] = useState('');
  const [minVisitesFilter, setMinVisitesFilter] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newClient, setNewClient] = useState({ prenom: '', nom: '', email: '', telephone: '', adresse: '', date_naissance: '' });
  const [syncing, setSyncing] = useState(false);
  // Campagne
  const [campSujet, setCampSujet] = useState('');
  const [campContenu, setCampContenu] = useState('');
  const [campTarget, setCampTarget] = useState('tous');
  const [campSending, setCampSending] = useState(false);
  const [campagnes, setCampagnes] = useState([]);
  // Import
  const [importing, setImporting] = useState(false);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (sourceFilter) params.set('source', sourceFilter);
      if (optInFilter) params.set('opt_in', optInFilter);
      if (minVisitesFilter > 0) params.set('min_visites', minVisitesFilter);
      const res = await api.get(`/clients?${params}`);
      setClients(res.data);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  }, [search, sourceFilter, optInFilter, minVisitesFilter]);

  const loadStats = useCallback(async () => {
    try { const res = await api.get('/clients/stats'); setStats(res.data); } catch {}
    try { const res = await api.get('/clients/dashboard'); setDashboard(res.data); } catch {}
  }, []);

  const loadCampagnes = useCallback(async () => {
    try { const res = await api.get('/clients/campagnes/historique'); setCampagnes(res.data); } catch {}
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);
  useEffect(() => { loadStats(); loadCampagnes(); }, [loadStats, loadCampagnes]);

  const openClient = async (c) => {
    try {
      const res = await api.get(`/clients/${c.id}`);
      setSelected(res.data);
      setEditForm({ prenom: res.data.prenom || '', nom: res.data.nom || '', email: res.data.email || '', telephone: res.data.telephone || '', adresse: res.data.adresse || '', date_naissance: res.data.date_naissance || '', notes: res.data.notes || '' });
      setEditing(false);
      setConfirmDelete(false);
    } catch { toast.error('Erreur'); }
  };

  const saveClient = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await api.put(`/clients/${selected.id}`, editForm);
      setSelected({ ...selected, ...res.data });
      setEditing(false);
      toast.success('Client modifie');
      loadClients(); loadStats();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  const deleteClient = async () => {
    if (!selected) return;
    try {
      await api.delete(`/clients/${selected.id}`);
      toast.success('Client supprime');
      setSelected(null);
      loadClients(); loadStats();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };

  const addClient = async () => {
    if (!newClient.email.includes('@')) { toast.error('Email invalide'); return; }
    try {
      await api.post('/clients', { ...newClient, source: 'manuel' });
      toast.success('Client ajoute');
      setShowAdd(false);
      setNewClient({ prenom: '', nom: '', email: '', telephone: '', adresse: '', date_naissance: '' });
      loadClients(); loadStats();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };

  const syncRoue = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/clients/sync-roue');
      toast.success(`${res.data.synced} clients synchronises depuis la Roue`);
      loadClients(); loadStats();
    } catch { toast.error('Erreur sync'); }
    setSyncing(false);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/clients/import-csv', formData);
      toast.success(`${res.data.imported} clients importes`);
      loadClients(); loadStats();
    } catch { toast.error('Erreur import'); }
    setImporting(false);
    e.target.value = '';
  };

  const handleImportZenchef = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const optInOnly = window.confirm(
      "Import Zenchef — Choisissez la politique RGPD :\n\n" +
      "OK = Importer UNIQUEMENT les clients opt-in email (100% légal, ~475 contacts)\n" +
      "Annuler = Importer TOUS les clients avec email valide (risqué juridiquement)"
    );
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/clients/import-zenchef?opt_in_only=${optInOnly}`, formData);
      const d = res.data;
      toast.success(
        `Zenchef : ${d.imported} nouveaux, ${d.updated} mis à jour` +
        (d.skipped_blacklist ? ` · ${d.skipped_blacklist} blacklistés ignorés` : '') +
        (d.skipped_no_optin ? ` · ${d.skipped_no_optin} sans opt-in ignorés` : ''),
        { duration: 6000 }
      );
      loadClients(); loadStats();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur import Zenchef'); }
    setImporting(false);
    e.target.value = '';
  };

  const sendCampagne = async () => {
    if (!campSujet.trim() || !campContenu.trim()) { toast.error('Sujet et contenu requis'); return; }
    setCampSending(true);
    try {
      const body = { sujet: campSujet, contenu: campContenu };
      if (campTarget === 'tous') body.tous = true;
      else if (campTarget.startsWith('source:')) body.source = campTarget.split(':')[1];
      const res = await api.post('/clients/campagne', body);
      toast.success(`${res.data.sent} emails envoyes`);
      setCampSujet(''); setCampContenu('');
      loadCampagnes();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur envoi'); }
    setCampSending(false);
  };

  return (
    <div className="space-y-4" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[rgb(var(--lm-text))] flex items-center gap-2">
            <Users size={20} className="text-[rgb(var(--lm-gold))]" /> Clients
          </h1>
          <p className="text-xs text-[rgb(var(--lm-text-muted))]">Base centralisee de tous vos clients</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={syncRoue} disabled={syncing}
            className="border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-muted))] text-xs" data-testid="sync-roue">
            {syncing ? <Loader2 size={12} className="animate-spin mr-1" /> : <Gamepad2 size={12} className="mr-1" />}
            Sync Roue
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)}
            className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] text-xs" data-testid="add-client">
            <Plus size={12} className="mr-1" /> Nouveau
          </Button>
        </div>
      </div>

      {/* Stats cards (Liste/Campagne tabs only) */}
      {stats && tab !== 'dashboard' && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <StatCard label="Total clients" value={stats.total} icon={<Users size={14} />} testId="stat-total" />
          <StatCard label="Avec email" value={stats.with_email} icon={<Mail size={14} />} testId="stat-email" />
          <StatCard label="Avec telephone" value={stats.with_phone} icon={<Phone size={14} />} testId="stat-phone" />
          <StatCard label="Jeu fidelite" value={stats.sources?.jeu || 0} icon={<Gamepad2 size={14} />} testId="stat-jeu" />
          <StatCard label="Zenchef" value={stats.sources?.zenchef || 0} icon={<UserCheck size={14} />} testId="stat-zenchef" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-lg bg-[rgb(var(--lm-surface))]">
        {[
          { id: 'dashboard', label: 'Tableau', icon: Users },
          { id: 'liste', label: 'Liste', icon: Users },
          { id: 'campagne', label: 'Campagne email', icon: Mail }
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-medium transition-all ${
              tab === t.id ? 'bg-[rgb(var(--lm-gold)/0.1)] text-[rgb(var(--lm-gold))]' : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]'
            }`} data-testid={`clients-tab-${t.id}`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {/* ═══ TAB: DASHBOARD (action-first) ═══ */}
      {tab === 'dashboard' && dashboard && (
        <div className="space-y-4" data-testid="clients-dashboard">
          {/* Hero trio */}
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            <button onClick={() => { setMinVisitesFilter(10); setOptInFilter(''); setSourceFilter(''); setTab('liste'); }}
              className="bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200 hover:border-amber-400 rounded-2xl p-3 md:p-4 text-left transition-all hover:shadow-lg" data-testid="hero-vip">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-amber-500 flex items-center justify-center shadow-md mb-2">
                <span className="text-white text-base md:text-lg">⭐</span>
              </div>
              <p className="text-3xl md:text-4xl font-black text-amber-700 leading-none">{dashboard.vip}</p>
              <p className="text-[10px] md:text-xs font-bold text-amber-700/80 mt-1 uppercase tracking-wider">VIP</p>
              <p className="text-[9px] md:text-[10px] text-amber-600/60 mt-0.5">10+ visites</p>
            </button>

            <button onClick={() => { setMinVisitesFilter(4); setOptInFilter(''); setSourceFilter(''); setTab('liste'); }}
              className="bg-gradient-to-br from-blue-50 to-sky-50 border-2 border-blue-200 hover:border-blue-400 rounded-2xl p-3 md:p-4 text-left transition-all hover:shadow-lg" data-testid="hero-habitues">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-blue-500 flex items-center justify-center shadow-md mb-2">
                <span className="text-white text-base md:text-lg">🔵</span>
              </div>
              <p className="text-3xl md:text-4xl font-black text-blue-700 leading-none">{dashboard.habitues}</p>
              <p className="text-[10px] md:text-xs font-bold text-blue-700/80 mt-1 uppercase tracking-wider">Habitués</p>
              <p className="text-[9px] md:text-[10px] text-blue-600/60 mt-0.5">4 à 9 visites</p>
            </button>

            <button onClick={() => { setOptInFilter('yes'); setMinVisitesFilter(0); setSourceFilter(''); setTab('liste'); }}
              className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 hover:border-emerald-400 rounded-2xl p-3 md:p-4 text-left transition-all hover:shadow-lg" data-testid="hero-optin">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-emerald-500 flex items-center justify-center shadow-md mb-2">
                <Mail size={16} className="text-white md:hidden" />
                <Mail size={20} className="text-white hidden md:block" />
              </div>
              <p className="text-3xl md:text-4xl font-black text-emerald-700 leading-none">{dashboard.optin}</p>
              <p className="text-[10px] md:text-xs font-bold text-emerald-700/80 mt-1 uppercase tracking-wider">Opt-in</p>
              <p className="text-[9px] md:text-[10px] text-emerald-600/60 mt-0.5">marketing OK</p>
            </button>
          </div>

          {/* Primary action — Lancer campagne fidélité */}
          <button onClick={() => { sessionStorage.setItem('patron_tab', 'crm-campagne'); window.location.reload(); }}
            className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-xl p-4 flex items-center justify-between shadow-lg shadow-rose-500/20 transition-all group"
            data-testid="dashboard-go-campaign">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Send size={20} />
              </div>
              <div className="text-left">
                <p className="font-bold text-sm">CAMPAGNE FIDÉLITÉ</p>
                <p className="text-xs text-white/80">Email vers vos {dashboard.optin} clients opt-in</p>
              </div>
            </div>
            <span className="text-white/60 group-hover:translate-x-1 transition-transform">→</span>
          </button>

          {/* Quick actions row */}
          <div className="grid grid-cols-3 gap-2">
            <Button size="sm" variant="outline" onClick={syncRoue} disabled={syncing}
              className="bg-white border border-gray-200 hover:border-purple-300 rounded-xl p-3 flex flex-col items-center gap-1.5 transition-all hover:shadow-sm h-auto" data-testid="quick-sync-roue">
              {syncing ? <Loader2 size={16} className="animate-spin text-purple-600" /> : <Gamepad2 size={16} className="text-purple-600" />}
              <span className="text-[10px] font-bold text-gray-700 text-center">Sync Roue ({stats?.sources?.jeu || 0})</span>
            </Button>
            <label className="cursor-pointer">
              <input type="file" accept=".csv" onChange={handleImportZenchef} className="hidden" data-testid="quick-import-zenchef-input" />
              <div className="bg-white border border-gray-200 hover:border-rose-300 rounded-xl p-3 flex flex-col items-center gap-1.5 transition-all hover:shadow-sm h-full">
                {importing ? <Loader2 size={16} className="animate-spin text-rose-600" /> : <Upload size={16} className="text-rose-600" />}
                <span className="text-[10px] font-bold text-gray-700 text-center">Import Zenchef</span>
              </div>
            </label>
            <button onClick={() => setShowAdd(true)}
              className="bg-white border border-gray-200 hover:border-amber-300 rounded-xl p-3 flex flex-col items-center gap-1.5 transition-all hover:shadow-sm" data-testid="quick-add-client">
              <Plus size={16} className="text-amber-600" />
              <span className="text-[10px] font-bold text-gray-700 text-center">Ajouter</span>
            </button>
          </div>

          {/* Top VIP list */}
          {dashboard.top_vip && dashboard.top_vip.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="top-vip-list">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                  <span>⭐</span> Top fidèles
                </h3>
                <button onClick={() => { setMinVisitesFilter(5); setSourceFilter(''); setOptInFilter(''); setTab('liste'); }}
                  className="text-[10px] text-amber-600 font-bold hover:underline">
                  Voir tout →
                </button>
              </div>
              <div className="space-y-1">
                {dashboard.top_vip.slice(0, 6).map((c, i) => (
                  <button key={c.id} onClick={() => openClient(c)}
                    className="w-full flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-amber-50/50 transition-colors text-left">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[10px] font-mono text-gray-300 w-4">{i + 1}.</span>
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-gray-900 truncate">{c.prenom} {c.nom}</p>
                        <p className="text-[10px] text-gray-500 truncate">
                          {c.statut_client || ''} {c.opt_in_email ? ' · ✉️ opt-in' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">
                        {c.nb_visites} visites
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Mini stats footer */}
          <div className="grid grid-cols-4 gap-2">
            <StatCard label="Total" value={dashboard.total} icon={<Users size={12} />} testId="mini-total" />
            <StatCard label="Réguliers" value={dashboard.reguliers} icon={<UserCheck size={12} />} testId="mini-reg" />
            <StatCard label="Jeu" value={stats?.sources?.jeu || 0} icon={<Gamepad2 size={12} />} testId="mini-jeu" />
            <StatCard label="Zenchef" value={stats?.sources?.zenchef || 0} icon={<UserCheck size={12} />} testId="mini-zen" />
          </div>
        </div>
      )}

      {/* ═══ TAB: LISTE ═══ */}
      {tab === 'liste' && (
        selected ? (
          <ClientDetail client={selected} editForm={editForm} setEditForm={setEditForm}
            editing={editing} setEditing={setEditing} saving={saving} saveClient={saveClient}
            confirmDelete={confirmDelete} setConfirmDelete={setConfirmDelete} deleteClient={deleteClient}
            onBack={() => setSelected(null)} />
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
                  className="pl-9 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-sm" data-testid="search-clients" />
              </div>
              <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
                className="px-3 py-1.5 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] text-xs text-[rgb(var(--lm-text))]"
                data-testid="filter-source">
                <option value="">Toutes sources</option>
                {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={optInFilter} onChange={e => setOptInFilter(e.target.value)}
                className="px-3 py-1.5 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] text-xs text-[rgb(var(--lm-text))]"
                data-testid="filter-optin">
                <option value="">Tous opt-in</option>
                <option value="yes">✅ Opt-in OK</option>
                <option value="no">⚠️ Sans opt-in</option>
              </select>
              <select value={minVisitesFilter} onChange={e => setMinVisitesFilter(parseInt(e.target.value))}
                className="px-3 py-1.5 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] text-xs text-[rgb(var(--lm-text))]"
                data-testid="filter-visites">
                <option value="0">Toutes fréquences</option>
                <option value="10">⭐ VIP (10+ visites)</option>
                <option value="4">🔵 Habitués (4+)</option>
                <option value="2">🟢 Réguliers (2+)</option>
                <option value="1">🆕 Au moins 1 visite</option>
              </select>
              <a href={`${API}/clients/export-csv`} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-muted))] h-9 text-xs" data-testid="export-csv">
                  <Download size={12} className="mr-1" /> CSV
                </Button>
              </a>
              <label className="cursor-pointer">
                <input type="file" accept=".csv" onChange={handleImport} className="hidden" data-testid="import-csv-input" />
                <Button size="sm" variant="outline" className="border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-muted))] h-9 text-xs pointer-events-none" data-testid="import-csv" tabIndex={-1}>
                  {importing ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />}
                  Import
                </Button>
              </label>
              <label className="cursor-pointer">
                <input type="file" accept=".csv" onChange={handleImportZenchef} className="hidden" data-testid="import-zenchef-input" />
                <Button size="sm" className="bg-[rgb(var(--lm-gold))] hover:bg-[rgb(var(--lm-gold))]/90 text-[rgb(var(--lm-navy))] h-9 text-xs font-bold pointer-events-none" data-testid="import-zenchef" tabIndex={-1}>
                  {importing ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />}
                  Zenchef
                </Button>
              </label>
            </div>

            <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{clients.length} client(s)</p>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[rgb(var(--lm-gold))]" /></div>
            ) : clients.length === 0 ? (
              <p className="text-center text-sm text-[rgb(var(--lm-text-muted))] py-12">Aucun client</p>
            ) : (
              <div className="space-y-1">
                {clients.map(c => (
                  <button key={c.id} onClick={() => openClient(c)}
                    className="w-full lm-card px-4 py-3 text-left hover:border-[rgb(var(--lm-gold)/0.2)] transition-colors" data-testid={`client-row-${c.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[rgb(var(--lm-text))] truncate">{c.prenom} {c.nom}</p>
                        <p className="text-xs text-[rgb(var(--lm-text-muted))] truncate">{c.email} {c.telephone ? `| ${c.telephone}` : ''}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 ml-2">
                        {(c.sources || []).map(s => {
                          const info = SOURCE_LABELS[s];
                          return info ? <span key={s} className={`px-1.5 py-0.5 rounded text-[9px] ${info.color}`}>{info.label}</span> : null;
                        })}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )
      )}

      {/* ═══ TAB: CAMPAGNE ═══ */}
      {tab === 'campagne' && (
        <div className="space-y-4">
          <div className="lm-card p-4 space-y-3">
            <h2 className="text-xs font-semibold text-[rgb(var(--lm-text))] flex items-center gap-1.5">
              <Send size={13} className="text-[rgb(var(--lm-gold))]" /> Nouvelle campagne
            </h2>

            <div>
              <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Destinataires</label>
              <select value={campTarget} onChange={e => setCampTarget(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] text-sm text-[rgb(var(--lm-text))]"
                data-testid="camp-target">
                <option value="tous">Tous les clients ({stats?.total || 0})</option>
                {Object.entries(SOURCE_LABELS).map(([k, v]) => (
                  <option key={k} value={`source:${k}`}>{v.label} ({stats?.sources?.[k] || 0})</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Sujet</label>
              <Input value={campSujet} onChange={e => setCampSujet(e.target.value)}
                placeholder="Ex: Notre nouveau menu du mois !" className="mt-1 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" data-testid="camp-sujet" />
              <p className="text-[9px] text-[rgb(var(--lm-text-muted))] mt-0.5">Utilisez {'{{PRENOM}}'} pour personnaliser</p>
            </div>

            <div>
              <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Contenu</label>
              <textarea value={campContenu} onChange={e => setCampContenu(e.target.value)}
                placeholder="Bonjour {{PRENOM}}, nous avons le plaisir de vous annoncer..."
                rows={5} className="w-full mt-1 px-3 py-2 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] text-sm text-[rgb(var(--lm-text))] resize-none outline-none"
                data-testid="camp-contenu" />
            </div>

            <Button onClick={sendCampagne} disabled={campSending}
              className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] w-full" data-testid="send-campagne">
              {campSending ? <Loader2 size={14} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
              Envoyer la campagne
            </Button>
          </div>

          {campagnes.length > 0 && (
            <div className="lm-card p-4">
              <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Historique</h2>
              <div className="space-y-1.5">
                {campagnes.map(c => (
                  <div key={c.id} className="flex items-center justify-between text-xs py-2 px-2 rounded-lg hover:bg-[rgb(var(--lm-surface))]">
                    <div>
                      <p className="text-[rgb(var(--lm-text))] font-medium">{c.sujet}</p>
                      <p className="text-[rgb(var(--lm-text-muted))] text-[10px]">{c.created_at?.slice(0, 10)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[rgb(var(--lm-green))] text-[10px]">{c.nb_envoyes} envoyes</span>
                      {c.nb_echecs > 0 && <span className="text-red-400 text-[10px]">{c.nb_echecs} echecs</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal: Ajouter client */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" data-testid="add-client-modal">
          <div className="lm-card p-5 max-w-md w-full space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-[rgb(var(--lm-text))]">Nouveau client</h2>
              <button onClick={() => setShowAdd(false)} className="text-[rgb(var(--lm-text-muted))]"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <FieldInput label="Prenom" value={newClient.prenom} onChange={v => setNewClient(f => ({ ...f, prenom: v }))} testId="new-prenom" />
              <FieldInput label="Nom" value={newClient.nom} onChange={v => setNewClient(f => ({ ...f, nom: v }))} testId="new-nom" />
            </div>
            <FieldInput label="Email *" type="email" value={newClient.email} onChange={v => setNewClient(f => ({ ...f, email: v }))} testId="new-email" />
            <FieldInput label="Telephone" type="tel" value={newClient.telephone} onChange={v => setNewClient(f => ({ ...f, telephone: v }))} testId="new-telephone" />
            <FieldInput label="Adresse" value={newClient.adresse} onChange={v => setNewClient(f => ({ ...f, adresse: v }))} testId="new-adresse" />
            <FieldInput label="Date de naissance" type="date" value={newClient.date_naissance} onChange={v => setNewClient(f => ({ ...f, date_naissance: v }))} testId="new-dob" />
            <div className="flex gap-2 justify-end pt-1">
              <button onClick={() => setShowAdd(false)} className="text-xs text-[rgb(var(--lm-text-muted))]">Annuler</button>
              <Button size="sm" onClick={addClient} className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] text-xs" data-testid="confirm-add-client">
                <UserCheck size={12} className="mr-1" /> Ajouter
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══ Client Detail View ═══ */
const ClientDetail = ({ client, editForm, setEditForm, editing, setEditing, saving, saveClient, confirmDelete, setConfirmDelete, deleteClient, onBack }) => (
  <div className="space-y-3">
    <button onClick={onBack} className="flex items-center gap-1 text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] transition-colors" data-testid="back-to-list">
      <ArrowLeft size={12} /> Retour a la liste
    </button>

    <div className="lm-card p-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-[rgb(var(--lm-text))]">{client.prenom} {client.nom}</h2>
          <p className="text-xs text-[rgb(var(--lm-text-muted))]">{client.email}</p>
        </div>
        <div className="flex items-center gap-1.5">
          {(client.sources || []).map(s => {
            const info = SOURCE_LABELS[s];
            return info ? <span key={s} className={`px-1.5 py-0.5 rounded text-[9px] ${info.color}`}>{info.label}</span> : null;
          })}
        </div>
      </div>
    </div>

    <div className="lm-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Informations</h2>
        {!editing && (
          <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-xs text-[rgb(var(--lm-gold))] hover:underline" data-testid="edit-client-btn">
            <Pencil size={11} /> Modifier
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <FieldInput label="Prenom" value={editForm.prenom} onChange={v => setEditForm(f => ({ ...f, prenom: v }))} testId="edit-prenom" />
            <FieldInput label="Nom" value={editForm.nom} onChange={v => setEditForm(f => ({ ...f, nom: v }))} testId="edit-nom" />
          </div>
          <FieldInput label="Email" type="email" value={editForm.email} onChange={v => setEditForm(f => ({ ...f, email: v }))} testId="edit-email" />
          <div className="grid grid-cols-2 gap-2">
            <FieldInput label="Telephone" type="tel" value={editForm.telephone} onChange={v => setEditForm(f => ({ ...f, telephone: v }))} testId="edit-telephone" />
            <FieldInput label="Date naissance" type="date" value={editForm.date_naissance} onChange={v => setEditForm(f => ({ ...f, date_naissance: v }))} testId="edit-dob" />
          </div>
          <FieldInput label="Adresse" value={editForm.adresse} onChange={v => setEditForm(f => ({ ...f, adresse: v }))} testId="edit-adresse" />
          <div>
            <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Notes</label>
            <textarea value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
              rows={2} className="w-full mt-1 px-3 py-2 rounded-lg bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-xs text-[rgb(var(--lm-text))] resize-none outline-none" data-testid="edit-notes" />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button onClick={() => setEditing(false)} className="text-xs text-[rgb(var(--lm-text-muted))]" data-testid="cancel-edit">Annuler</button>
            <Button size="sm" onClick={saveClient} disabled={saving} className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] h-7 text-xs" data-testid="save-client">
              {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />} Sauvegarder
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {[
            { icon: Phone, label: 'Telephone', value: client.telephone || '\u2014' },
            { icon: MapPin, label: 'Adresse', value: client.adresse || '\u2014' },
            { icon: CalendarDays, label: 'Date de naissance', value: client.date_naissance || '\u2014' },
            { icon: Clock, label: 'Inscrit le', value: client.created_at?.slice(0, 10) || '\u2014' },
          ].map((item, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 border-b border-[rgb(var(--lm-border)/0.3)] last:border-0">
              <span className="flex items-center gap-1.5 text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">
                <item.icon size={10} /> {item.label}
              </span>
              <span className="text-xs text-[rgb(var(--lm-text))]">{item.value}</span>
            </div>
          ))}
          {client.notes && (
            <div className="pt-1">
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1">Notes</p>
              <p className="text-xs text-[rgb(var(--lm-text-secondary))] italic">{client.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>

    {/* Vouchers history */}
    {client.vouchers?.length > 0 && (
      <div className="lm-card p-4">
        <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Bons du jeu de fidelite</h2>
        <div className="space-y-1">
          {client.vouchers.map(v => (
            <div key={v.id || v.code} className="flex items-center justify-between text-xs py-1.5 px-2 rounded-lg hover:bg-[rgb(var(--lm-surface))]">
              <div className="flex items-center gap-2">
                <span>{v.prize_emoji}</span>
                <span className="text-[rgb(var(--lm-text-secondary))]">{v.prize_label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[rgb(var(--lm-text-muted))] text-[10px]">{v.code}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${v.used ? 'bg-[rgb(var(--lm-green)/0.15)] text-[rgb(var(--lm-green))]' : 'bg-[rgb(var(--lm-gold)/0.1)] text-[rgb(var(--lm-gold))]'}`}>
                  {v.used ? 'Utilise' : 'Actif'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Delete */}
    <div className="lm-card p-4">
      {!confirmDelete ? (
        <button onClick={() => setConfirmDelete(true)} className="flex items-center gap-1 text-xs text-red-400/60 hover:text-red-400 transition-colors" data-testid="delete-client-btn">
          <Trash2 size={12} /> Supprimer ce client
        </button>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-red-400">Supprimer {client.prenom} {client.nom} ?</p>
          <div className="flex gap-2">
            <button onClick={() => setConfirmDelete(false)} className="text-xs text-[rgb(var(--lm-text-muted))]" data-testid="cancel-delete">Annuler</button>
            <Button size="sm" onClick={deleteClient} className="bg-red-500 text-white h-7 text-xs hover:bg-red-600" data-testid="confirm-delete">
              Confirmer
            </Button>
          </div>
        </div>
      )}
    </div>
  </div>
);

/* ═══ Shared Components ═══ */
const FieldInput = ({ label, type = 'text', value, onChange, testId }) => (
  <div>
    <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">{label}</label>
    <Input type={type} value={value} onChange={e => onChange(e.target.value)}
      className="mt-1 h-8 text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid={testId} />
  </div>
);

const StatCard = ({ label, value, icon, testId }) => (
  <div className="lm-card px-3 py-3 text-center">
    <div className="flex items-center justify-center gap-1.5 text-[rgb(var(--lm-text-muted))] mb-1">{icon}<span className="text-[9px] uppercase tracking-wider">{label}</span></div>
    <p className="text-lg font-bold text-[rgb(var(--lm-gold))] font-mono" data-testid={testId}>{value}</p>
  </div>
);

export default ClientsPage;
